import React, { useState, useEffect } from 'react';
import { apiClient } from '../../App';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Settings, Loader2, Play, Clock, CheckCircle, Calendar, Bell, Link, FileText, X, Mail, UserPlus, Search, AlertCircle, User, ChevronDown, ChevronUp, Gift, Trophy, PartyPopper, Send, MessageCircle } from 'lucide-react';

const AdminAutomations = () => {
  const [automations, setAutomations] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Test email state
  const [sendingTestId, setSendingTestId] = useState(null);
  
  // Debug state
  const [debugEmail, setDebugEmail] = useState('');
  const [debugLoading, setDebugLoading] = useState(false);
  const [debugResult, setDebugResult] = useState(null);
  
  // Collapsible sections state
  const [welcomeSectionOpen, setWelcomeSectionOpen] = useState(false);
  const [systemMessagesOpen, setSystemMessagesOpen] = useState(false);
  
  // Welcome message state (self-registration)
  const [welcomeMessage, setWelcomeMessage] = useState({ title: '', message: '' });
  const [welcomeEditing, setWelcomeEditing] = useState(false);
  const [welcomeSaving, setWelcomeSaving] = useState(false);
  
  // Admin welcome message state (admin-created users)
  const [adminWelcomeMessage, setAdminWelcomeMessage] = useState({ title: '', message: '' });
  const [adminWelcomeEditing, setAdminWelcomeEditing] = useState(false);
  const [adminWelcomeSaving, setAdminWelcomeSaving] = useState(false);
  
  // Trial welcome message state (free trial users)
  const [trialWelcomeMessage, setTrialWelcomeMessage] = useState({ title: '', message: '' });
  const [trialWelcomeEditing, setTrialWelcomeEditing] = useState(false);
  const [trialWelcomeSaving, setTrialWelcomeSaving] = useState(false);
  
  // Birthday message state
  const [birthdayMessage, setBirthdayMessage] = useState({ title: '', message: '' });
  const [birthdayEditing, setBirthdayEditing] = useState(false);
  const [birthdaySaving, setBirthdaySaving] = useState(false);

  // Community welcome post (auto-posted on Community Board when new user signs up)
  const [communityWelcome, setCommunityWelcome] = useState({ enabled: true, content: '', default: '' });
  const [communityWelcomeEditing, setCommunityWelcomeEditing] = useState(false);
  const [communityWelcomeSaving, setCommunityWelcomeSaving] = useState(false);
  
  // Month-end celebration message state
  const [monthEndMessage, setMonthEndMessage] = useState({ title: '', message: '', auto_send: true });
  const [monthEndEditing, setMonthEndEditing] = useState(false);
  const [monthEndSaving, setMonthEndSaving] = useState(false);

  // Member promo block state (footer of all client-facing emails)
  const [memberPromo, setMemberPromo] = useState({ enabled: true, label: '', body: '', contact_email: '' });
  const [memberPromoDefaults, setMemberPromoDefaults] = useState(null);
  const [memberPromoEditing, setMemberPromoEditing] = useState(false);
  const [memberPromoSaving, setMemberPromoSaving] = useState(false);
  
  const [form, setForm] = useState({
    name: '',
    trigger_type: 'inactivity',
    trigger_days: 3,
    action_id: '',
    message_subject: '',
    message_body: '',
    gif_url: '',
    links: [],
    require_monthly_activity: false,
    send_email: true,
    is_active: true,
    is_trial_automation: false,
    target_audience: 'all'
  });
  
  // Trial automations seeding
  const [seedingTrialAutomations, setSeedingTrialAutomations] = useState(false);
  
  // Recalculate action streaks state
  const [recalculatingStreaks, setRecalculatingStreaks] = useState(false);

  useEffect(() => {
    fetchData();
    fetchWelcomeMessage();
    fetchAdminWelcomeMessage();
    fetchTrialWelcomeMessage();
    fetchBirthdayMessage();
    fetchCommunityWelcome();
    fetchMonthEndMessage();
    fetchMemberPromo();
  }, []);

  const fetchData = async () => {
    try {
      const [automationsRes, actionsRes] = await Promise.all([
        apiClient.get('/automations'),
        apiClient.get('/actions')
      ]);
      setAutomations(automationsRes.data);
      setActions(actionsRes.data);
    } catch (err) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchWelcomeMessage = async () => {
    try {
      const res = await apiClient.get('/admin/welcome-message');
      setWelcomeMessage(res.data);
    } catch (err) {
      console.error('Failed to fetch welcome message:', err);
    }
  };

  const saveWelcomeMessage = async () => {
    setWelcomeSaving(true);
    try {
      await apiClient.put('/admin/welcome-message', welcomeMessage);
      toast.success('Welcome message updated');
      setWelcomeEditing(false);
    } catch (err) {
      toast.error('Failed to update welcome message');
    } finally {
      setWelcomeSaving(false);
    }
  };

  const fetchAdminWelcomeMessage = async () => {
    try {
      const res = await apiClient.get('/admin/admin-welcome-message');
      setAdminWelcomeMessage(res.data);
    } catch (err) {
      console.error('Failed to fetch admin welcome message:', err);
    }
  };

  const saveAdminWelcomeMessage = async () => {
    setAdminWelcomeSaving(true);
    try {
      await apiClient.put('/admin/admin-welcome-message', adminWelcomeMessage);
      toast.success('Admin welcome message updated');
      setAdminWelcomeEditing(false);
    } catch (err) {
      toast.error('Failed to update admin welcome message');
    } finally {
      setAdminWelcomeSaving(false);
    }
  };

  const fetchTrialWelcomeMessage = async () => {
    try {
      const res = await apiClient.get('/admin/trial-welcome-message');
      setTrialWelcomeMessage(res.data);
    } catch (err) {
      console.error('Failed to fetch trial welcome message:', err);
    }
  };

  const saveTrialWelcomeMessage = async () => {
    setTrialWelcomeSaving(true);
    try {
      await apiClient.put('/admin/trial-welcome-message', trialWelcomeMessage);
      toast.success('Trial welcome message updated');
      setTrialWelcomeEditing(false);
    } catch (err) {
      toast.error('Failed to update trial welcome message');
    } finally {
      setTrialWelcomeSaving(false);
    }
  };

  const fetchBirthdayMessage = async () => {
    try {
      const res = await apiClient.get('/admin/birthday-message');
      setBirthdayMessage(res.data);
    } catch (err) {
      console.error('Failed to fetch birthday message:', err);
    }
  };

  const saveBirthdayMessage = async () => {
    setBirthdaySaving(true);
    try {
      await apiClient.put('/admin/birthday-message', birthdayMessage);
      toast.success('Birthday message updated');
      setBirthdayEditing(false);
    } catch (err) {
      toast.error('Failed to update birthday message');
    } finally {
      setBirthdaySaving(false);
    }
  };

  const fetchCommunityWelcome = async () => {
    try {
      const res = await apiClient.get('/admin/community-welcome-post');
      setCommunityWelcome(res.data);
    } catch (err) {
      console.error('Failed to fetch community welcome post:', err);
    }
  };

  const saveCommunityWelcome = async () => {
    setCommunityWelcomeSaving(true);
    try {
      await apiClient.put('/admin/community-welcome-post', {
        enabled: communityWelcome.enabled,
        content: communityWelcome.content,
      });
      toast.success('Community welcome post updated');
      setCommunityWelcomeEditing(false);
    } catch (err) {
      toast.error('Failed to update community welcome post');
    } finally {
      setCommunityWelcomeSaving(false);
    }
  };

  const toggleCommunityWelcome = async () => {
    const next = !communityWelcome.enabled;
    try {
      await apiClient.put('/admin/community-welcome-post', { enabled: next });
      setCommunityWelcome({ ...communityWelcome, enabled: next });
      toast.success(next ? 'Auto-welcome post enabled' : 'Auto-welcome post disabled');
    } catch (err) {
      toast.error('Failed to toggle welcome post');
    }
  };

  const fetchMonthEndMessage = async () => {
    try {
      const res = await apiClient.get('/admin/month-end-message');
      setMonthEndMessage(res.data);
    } catch (err) {
      console.error('Failed to fetch month-end message:', err);
    }
  };

  const saveMonthEndMessage = async () => {
    setMonthEndSaving(true);
    try {
      await apiClient.put('/admin/month-end-message', monthEndMessage);
      toast.success('Month-end celebration message updated');
      setMonthEndEditing(false);
    } catch (err) {
      toast.error('Failed to update month-end message');
    } finally {
      setMonthEndSaving(false);
    }
  };

  const fetchMemberPromo = async () => {
    try {
      const res = await apiClient.get('/admin/member-promo');
      const { defaults, ...rest } = res.data;
      setMemberPromo(rest);
      setMemberPromoDefaults(defaults);
    } catch (err) {
      console.error('Failed to fetch member promo:', err);
    }
  };

  const saveMemberPromo = async () => {
    setMemberPromoSaving(true);
    try {
      await apiClient.put('/admin/member-promo', memberPromo);
      toast.success('Members-Only Perk block updated');
      setMemberPromoEditing(false);
    } catch (err) {
      toast.error('Failed to update member promo');
    } finally {
      setMemberPromoSaving(false);
    }
  };

  const resetMemberPromo = async () => {
    if (!window.confirm('Reset the Members-Only Perk block to the default text?')) return;
    setMemberPromoSaving(true);
    try {
      await apiClient.post('/admin/member-promo/reset');
      await fetchMemberPromo();
      toast.success('Members-Only Perk reset to default');
      setMemberPromoEditing(false);
    } catch (err) {
      toast.error('Failed to reset member promo');
    } finally {
      setMemberPromoSaving(false);
    }
  };

  // Send test email for an automation
  const sendTestEmail = async (automationId, messageType = 'automation') => {
    setSendingTestId(automationId || messageType);
    try {
      const res = await apiClient.post('/admin/automations/send-test', {
        automation_id: automationId,
        message_type: messageType
      });
      toast.success(res.data.message || 'Test email sent! Check your inbox.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingTestId(null);
    }
  };

  const resetForm = () => {
    setForm({
      name: '',
      trigger_type: 'inactivity',
      trigger_days: 3,
      action_id: '',
      message_subject: '',
      message_body: '',
      gif_url: '',
      links: [],
      require_monthly_activity: false,
      send_email: true,
      is_active: true,
      is_trial_automation: false,
      target_audience: 'all'
    });
    setEditing(null);
  };

  const openEdit = (automation) => {
    setForm({
      name: automation.name,
      trigger_type: automation.trigger_type,
      trigger_days: automation.trigger_days,
      action_id: automation.action_id || '',
      message_subject: automation.message_subject,
      message_body: automation.message_body,
      gif_url: automation.gif_url || '',
      links: automation.links || [],
      require_monthly_activity: automation.require_monthly_activity || false,
      send_email: automation.send_email !== false,
      is_active: automation.is_active,
      is_trial_automation: automation.is_trial_automation || false,
      target_audience: automation.target_audience || 'all'
    });
    setEditing(automation.id);
    setDialogOpen(true);
  };

  const addLink = () => {
    setForm({
      ...form,
      links: [...form.links, { label: '', url: '', is_pdf: false }]
    });
  };

  const updateLink = (index, field, value) => {
    const newLinks = [...form.links];
    newLinks[index][field] = value;
    setForm({ ...form, links: newLinks });
  };

  const removeLink = (index) => {
    setForm({
      ...form,
      links: form.links.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.message_subject || !form.message_body) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (form.trigger_type === 'missing_action' && !form.action_id) {
      toast.error('Please select an action for missing action trigger');
      return;
    }

    if (form.trigger_type === 'scheduled_notification' && (form.trigger_days < 1 || form.trigger_days > 29)) {
      toast.error('Scheduled day must be between 1 and 29');
      return;
    }

    if (form.trigger_type === 'no_monthly_activity' && (form.trigger_days < 1 || form.trigger_days > 28)) {
      toast.error('No-activity nudge day must be between 1 and 28');
      return;
    }

    setSaving(true);
    try {
      const payload = { ...form, action_id: form.action_id || null };
      if (editing) {
        await apiClient.put(`/automations/${editing}`, payload);
        toast.success('Automation updated');
      } else {
        await apiClient.post('/automations', payload);
        toast.success('Automation created');
      }
      fetchData();
      setDialogOpen(false);
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save automation');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this automation?')) return;
    
    try {
      await apiClient.delete(`/automations/${id}`);
      toast.success('Automation deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete automation');
    }
  };

  const runAutomations = async () => {
    setRunning(true);
    try {
      const res = await apiClient.post('/automations/run');
      toast.success(res.data.message);
    } catch (err) {
      toast.error('Failed to run automations');
    } finally {
      setRunning(false);
    }
  };

  const seedTrialAutomations = async () => {
    setSeedingTrialAutomations(true);
    try {
      const res = await apiClient.post('/admin/trial-automations/seed');
      if (res.data.created && res.data.created.length > 0) {
        toast.success(res.data.message);
        fetchData(); // Refresh the list
      } else {
        toast.info(res.data.message);
      }
    } catch (err) {
      toast.error('Failed to seed trial automations');
    } finally {
      setSeedingTrialAutomations(false);
    }
  };

  const recalculateAllActionStreaks = async () => {
    if (!window.confirm('This will recalculate action streaks for ALL users. This ensures "Missing Action" automations use accurate data. Continue?')) {
      return;
    }
    setRecalculatingStreaks(true);
    try {
      const res = await apiClient.post('/admin/recalculate-all-action-streaks');
      toast.success(res.data.message);
    } catch (err) {
      toast.error('Failed to recalculate action streaks');
    } finally {
      setRecalculatingStreaks(false);
    }
  };

  const debugUserAutomation = async () => {
    if (!debugEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    setDebugLoading(true);
    setDebugResult(null);
    try {
      const res = await apiClient.get(`/automations/debug/${encodeURIComponent(debugEmail.trim())}`);
      setDebugResult(res.data);
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error('User not found with that email');
      } else {
        toast.error('Failed to check user status');
      }
      setDebugResult(null);
    } finally {
      setDebugLoading(false);
    }
  };

  const getActionName = (actionId) => {
    return actions.find(a => a.id === actionId)?.name || 'Unknown Action';
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 md:p-8 max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-stone-900">AUTOMATIONS</h1>
            <p className="text-stone-600 text-sm">Set up automated messages for user engagement</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={recalculateAllActionStreaks}
              disabled={recalculatingStreaks}
              className="border-stone-300 text-stone-700 hover:bg-stone-100"
              data-testid="recalculate-streaks-btn"
              title="Fix incorrect action data for all users"
            >
              {recalculatingStreaks ? <Loader2 className="animate-spin mr-2" size={16} /> : <Settings size={16} className="mr-2" />}
              Fix Action Data
            </Button>
            <Button
              variant="outline"
              onClick={runAutomations}
              disabled={running}
              className="border-stone-300 text-stone-700 hover:bg-stone-100"
              data-testid="run-automations-btn"
            >
              {running ? <Loader2 className="animate-spin mr-2" size={16} /> : <Play size={16} className="mr-2" />}
              Run Now
            </Button>
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-[#EFB342] text-black hover:bg-[#D99F30] font-bold" data-testid="add-automation-btn">
                  <Plus size={18} className="mr-2" />
                  Add Automation
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white border-stone-200 text-stone-900 max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl">
                    {editing ? 'Edit Automation' : 'Create Automation'}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div>
                    <Label>Name *</Label>
                    <Input
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="3-Day Inactive Reminder"
                      className="bg-stone-50 border-stone-200 mt-1"
                      data-testid="automation-name-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Trigger Type *</Label>
                      <Select value={form.trigger_type} onValueChange={(v) => setForm({ ...form, trigger_type: v, trigger_days: v === 'scheduled_notification' ? 14 : v === 'trial_day' ? 5 : v === 'renewal_reminder' ? 14 : v === 'no_monthly_activity' ? 7 : 3, is_trial_automation: v === 'trial_day', target_audience: v === 'trial_day' ? 'trial_only' : 'all' })}>
                        <SelectTrigger className="bg-stone-50 border-stone-200 mt-1" data-testid="automation-trigger-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-stone-200">
                          <SelectItem value="inactivity">User Inactivity</SelectItem>
                          <SelectItem value="missing_action">Missing Action</SelectItem>
                          <SelectItem value="no_monthly_activity">🌱 No Activity This Month</SelectItem>
                          <SelectItem value="renewal_reminder">🔄 Renewal Reminder</SelectItem>
                          <SelectItem value="scheduled_notification">📅 Scheduled Notification</SelectItem>
                          <SelectItem value="trial_day">🆕 Trial Day Trigger</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>{form.trigger_type === 'scheduled_notification' ? 'Day of Month (1-29) *' : form.trigger_type === 'trial_day' ? 'Trial Day (1-15) *' : form.trigger_type === 'renewal_reminder' ? 'Days Before Renewal *' : form.trigger_type === 'no_monthly_activity' ? 'Day of Month to Send (1-28) *' : 'Days *'}</Label>
                      <Input
                        type="number"
                        min="1"
                        max={form.trigger_type === 'scheduled_notification' ? 29 : form.trigger_type === 'trial_day' ? 15 : form.trigger_type === 'no_monthly_activity' ? 28 : 30}
                        value={form.trigger_days}
                        onChange={(e) => setForm({ ...form, trigger_days: parseInt(e.target.value) || 1 })}
                        className="bg-stone-50 border-stone-200 mt-1"
                        data-testid="automation-days-input"
                      />
                      {form.trigger_type === 'scheduled_notification' && (
                        <p className="text-xs text-stone-500 mt-1">e.g., 14 = sends on the 14th of each month (use 29 for end-of-month announcements)</p>
                      )}
                      {form.trigger_type === 'renewal_reminder' && (
                        <p className="text-xs text-stone-500 mt-1">e.g., 14 = sends 14 days before their annual subscription renewal date</p>
                      )}
                      {form.trigger_type === 'no_monthly_activity' && (
                        <p className="text-xs text-stone-500 mt-1">Sends on this day of the month to users with zero check-ins for the current month</p>
                      )}
                    </div>
                  </div>
                  {form.trigger_type === 'no_monthly_activity' && (
                    <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3">
                      <p className="text-sm text-emerald-400">
                        <Bell size={14} className="inline mr-2" />
                        On day <strong>{form.trigger_days}</strong> of each month, this fires (in-app + email) for any user who has not checked in even once since the 1st. Use tokens like <code>{'{first_name}'}</code> in your message. Each user only receives it once per month for this specific day.
                      </p>
                    </div>
                  )}
                  {form.trigger_type === 'renewal_reminder' && (
                    <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3">
                      <p className="text-sm text-purple-400">
                        <Calendar size={14} className="inline mr-2" />
                        This notification will be sent <strong>{form.trigger_days} days before</strong> each user's annual subscription renewal date. 
                        Make sure users have their <strong>Subscription Start Date</strong> set in their profile.
                      </p>
                    </div>
                  )}
                  {form.trigger_type === 'missing_action' && (
                    <div>
                      <Label>Action to Track *</Label>
                      <Select value={form.action_id} onValueChange={(v) => setForm({ ...form, action_id: v })}>
                        <SelectTrigger className="bg-stone-50 border-stone-200 mt-1" data-testid="automation-action-select">
                          <SelectValue placeholder="Select action" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-stone-200">
                          {actions.map(a => (
                            <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {form.trigger_type === 'scheduled_notification' && (
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                      <p className="text-sm text-blue-400">
                        <Bell size={14} className="inline mr-2" />
                        This notification will be sent to <strong>all users</strong> on day {form.trigger_days} of each month when automations are run.
                      </p>
                    </div>
                  )}
                  {form.trigger_type === 'trial_day' && (
                    <>
                      <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                        <p className="text-sm text-green-400">
                          <UserPlus size={14} className="inline mr-2" />
                          This notification will be sent to trial users on day <strong>{form.trigger_days}</strong> of their trial.
                        </p>
                      </div>
                      <div>
                        <Label>Target Audience *</Label>
                        <Select value={form.target_audience} onValueChange={(v) => setForm({ ...form, target_audience: v })}>
                          <SelectTrigger className="bg-stone-50 border-stone-300 mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-white border-stone-300">
                            <SelectItem value="trial_only">All Trial Users</SelectItem>
                            <SelectItem value="unconverted_trial">Unconverted Trial Users Only</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-stone-500 mt-1">
                          {form.target_audience === 'unconverted_trial' 
                            ? "Only trial users who have NOT yet upgraded to paid will receive this message"
                            : "All trial users will receive this message on their specified trial day"}
                        </p>
                      </div>
                    </>
                  )}
                  {form.trigger_type === 'missing_action' && (
                    <div className="flex items-center justify-between bg-stone-50 border border-stone-200 rounded-lg p-3">
                      <div>
                        <Label className="text-sm">Only Active Users</Label>
                        <p className="text-xs text-stone-500 mt-0.5">Only notify users who have collected points within the last 30 days (rolling)</p>
                      </div>
                      <Switch
                        checked={form.require_monthly_activity}
                        onCheckedChange={(checked) => setForm({ ...form, require_monthly_activity: checked })}
                        data-testid="automation-require-activity-switch"
                      />
                    </div>
                  )}
                  <div>
                    <Label>Message Subject *</Label>
                    <Input
                      value={form.message_subject}
                      onChange={(e) => setForm({ ...form, message_subject: e.target.value })}
                      placeholder="We miss you! 💪"
                      className="bg-stone-50 border-stone-200 mt-1"
                      data-testid="automation-subject-input"
                    />
                  </div>
                  <div>
                    <Label>Message Body *</Label>
                    <Textarea
                      value={form.message_body}
                      onChange={(e) => setForm({ ...form, message_body: e.target.value })}
                      placeholder="Hey {first_name}! We noticed you haven't checked in for a few days..."
                      className="bg-stone-50 border-stone-200 mt-1"
                      rows={4}
                      data-testid="automation-body-input"
                    />
                    <p className="text-xs text-stone-500 mt-1">
                      Personalization tokens: <code className="px-1 rounded bg-stone-100 text-stone-700">{'{first_name}'}</code> (member's first name), <code className="px-1 rounded bg-stone-100 text-stone-700">{'{points}'}</code> (current points). Also available in the subject.
                    </p>
                  </div>
                  
                  {/* GIF / Image URL */}
                  <div>
                    <Label>GIF / Image URL <span className="text-stone-500 text-xs font-normal">(optional — renders above the message)</span></Label>
                    <Input
                      value={form.gif_url}
                      onChange={(e) => setForm({ ...form, gif_url: e.target.value })}
                      placeholder="https://media.giphy.com/media/..."
                      className="bg-stone-50 border-stone-200 mt-1"
                      data-testid="automation-gif-url-input"
                    />
                    {form.gif_url && (
                      <div className="mt-2 rounded-lg overflow-hidden bg-stone-50 border border-stone-200 max-w-xs">
                        <img
                          src={form.gif_url}
                          alt="preview"
                          className="w-full h-auto"
                          onError={(e) => { e.target.style.display = 'none'; }}
                          data-testid="automation-gif-preview"
                        />
                      </div>
                    )}
                  </div>
                  
                  {/* Send Email toggle */}
                  <div className="flex items-center justify-between rounded-lg border border-stone-200 bg-stone-50/40 px-3 py-2.5">
                    <div>
                      <Label className="text-stone-800">Also send via email</Label>
                      <p className="text-xs text-stone-500 mt-0.5">
                        When on, this automation fires an email in addition to the in-app notification.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, send_email: !form.send_email })}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.send_email ? 'bg-[#EFB342]' : 'bg-stone-200'}`}
                      data-testid="automation-send-email-toggle"
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${form.send_email ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>
                  
                  {/* Links Section */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Links & Attachments</Label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={addLink}
                        className="text-[#EFB342] hover:text-[#D99F30] h-7 px-2"
                      >
                        <Plus size={14} className="mr-1" /> Add Link
                      </Button>
                    </div>
                    {form.links.length > 0 && (
                      <div className="space-y-2">
                        {form.links.map((link, idx) => (
                          <div key={idx} className="bg-stone-50 border border-stone-200 rounded-lg p-3 space-y-2">
                            <div className="flex items-center gap-2">
                              <Input
                                value={link.label}
                                onChange={(e) => updateLink(idx, 'label', e.target.value)}
                                placeholder="Link label (e.g., 'Veggie Recipe Guide')"
                                className="bg-white border-stone-300 flex-1 h-8 text-sm"
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => removeLink(idx)}
                                className="text-stone-600 hover:text-red-500 h-8 w-8"
                              >
                                <X size={14} />
                              </Button>
                            </div>
                            <div className="flex items-center gap-2">
                              <Input
                                value={link.url}
                                onChange={(e) => updateLink(idx, 'url', e.target.value)}
                                placeholder="https://..."
                                className="bg-white border-stone-300 flex-1 h-8 text-sm"
                              />
                              <button
                                type="button"
                                onClick={() => updateLink(idx, 'is_pdf', !link.is_pdf)}
                                className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors ${
                                  link.is_pdf 
                                    ? 'bg-red-500/20 text-red-400 border border-red-500/30' 
                                    : 'bg-stone-100 text-stone-600 border border-stone-300'
                                }`}
                              >
                                <FileText size={12} />
                                PDF
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {form.links.length === 0 && (
                      <p className="text-xs text-stone-400">No links added. Click &quot;Add Link&quot; to include URLs or PDF attachments.</p>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <Label>Active</Label>
                    <Switch
                      checked={form.is_active}
                      onCheckedChange={(checked) => setForm({ ...form, is_active: checked })}
                      data-testid="automation-active-switch"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={saving}
                    className="w-full bg-[#EFB342] text-black hover:bg-[#D99F30] font-bold"
                    data-testid="automation-submit-btn"
                  >
                    {saving ? <Loader2 className="animate-spin" size={18} /> : editing ? 'Update' : 'Create'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Info Banner */}
        <div className="bg-white border border-green-800/50 rounded-xl p-4 mb-6 flex items-start gap-3">
          <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={20} />
          <div className="text-sm text-stone-600">
            <p className="font-medium text-stone-900 mb-1">Email notifications are active</p>
            <p>Automated messages will be sent via email from rxgrit@buildnutrition.ca when triggered.</p>
          </div>
        </div>

        {/* Test & Debug Section */}
        <div className="bg-white border border-orange-800/50 rounded-2xl p-6 mb-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-12 h-12 bg-orange-500/20 rounded-xl flex items-center justify-center">
              <Search className="text-orange-400" size={24} />
            </div>
            <div>
              <h3 className="font-semibold text-stone-900">Test & Debug Automations</h3>
              <p className="text-sm text-stone-500">Check why a user isn't receiving automated messages</p>
            </div>
          </div>
          
          <div className="flex gap-3 mb-4">
            <Input
              placeholder="Enter user email address..."
              value={debugEmail}
              onChange={(e) => setDebugEmail(e.target.value)}
              className="bg-stone-50 border-stone-300 text-stone-900 flex-1"
              data-testid="debug-email-input"
              autoComplete="off"
              data-form-type="other"
              data-lpignore="true"
            />
            <Button
              onClick={debugUserAutomation}
              disabled={debugLoading}
              className="bg-orange-500 text-stone-900 hover:bg-orange-600"
              data-testid="debug-check-btn"
            >
              {debugLoading ? <Loader2 className="animate-spin mr-2" size={16} /> : <Search size={16} className="mr-2" />}
              Check User
            </Button>
          </div>
          
          {debugResult && (
            <div className="bg-stone-50 border border-stone-200 rounded-xl p-4 space-y-4">
              {/* User Info */}
              <div className="flex items-center gap-3 pb-3 border-b border-stone-200">
                <div className="w-10 h-10 bg-stone-100 rounded-full flex items-center justify-center">
                  <User className="text-stone-600" size={20} />
                </div>
                <div>
                  <p className="font-medium text-stone-900">{debugResult.user.name}</p>
                  <p className="text-sm text-stone-500">{debugResult.user.email}</p>
                </div>
              </div>
              
              {/* Activity Status */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-white rounded-lg p-3">
                  <p className="text-xs text-stone-500 mb-1">Days Inactive</p>
                  <p className={`text-xl font-bold ${debugResult.user.days_inactive >= 7 ? 'text-red-400' : debugResult.user.days_inactive >= 3 ? 'text-orange-400' : 'text-green-400'}`}>
                    {debugResult.user.days_inactive !== null ? debugResult.user.days_inactive : 'Never active'}
                  </p>
                </div>
                <div className="bg-white rounded-lg p-3">
                  <p className="text-xs text-stone-500 mb-1">Last Check-in</p>
                  <p className="text-sm text-stone-900">{debugResult.user.last_check_in || 'Never'}</p>
                </div>
                <div className="bg-white rounded-lg p-3">
                  <p className="text-xs text-stone-500 mb-1">Total Points</p>
                  <p className="text-xl font-bold text-[#EFB342]">{debugResult.user.total_points}</p>
                </div>
                <div className="bg-white rounded-lg p-3">
                  <p className="text-xs text-stone-500 mb-1">User Type</p>
                  <p className="text-sm text-stone-900 capitalize">{debugResult.user.user_type || 'Regular'}</p>
                </div>
              </div>
              
              {/* Automation Status */}
              <div>
                <p className="text-sm font-medium text-stone-900 mb-2">Automation Triggers</p>
                {debugResult.automations.length > 0 ? (
                  <div className="space-y-2">
                    {debugResult.automations.map((auto, idx) => (
                      <div key={idx} className={`flex items-center justify-between p-3 rounded-lg ${auto.would_trigger ? 'bg-green-500/10 border border-green-500/30' : 'bg-white'}`}>
                        <div>
                          <p className="text-sm text-stone-900">{auto.name}</p>
                          <p className="text-xs text-stone-500">{auto.reason}</p>
                        </div>
                        {auto.would_trigger ? (
                          <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded">Should Trigger</span>
                        ) : (
                          <span className="text-xs bg-stone-100 text-stone-500 px-2 py-1 rounded">Won't Trigger</span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-stone-500">No inactivity automations configured</p>
                )}
              </div>
              
              {/* Recent Notifications */}
              <div>
                <p className="text-sm font-medium text-stone-900 mb-2">Recent Notifications Received</p>
                {debugResult.recent_notifications.length > 0 ? (
                  <div className="space-y-2">
                    {debugResult.recent_notifications.map((notif, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-white rounded-lg">
                        <div>
                          <p className="text-sm text-stone-900">{notif.title}</p>
                          <p className="text-xs text-stone-500">{notif.type} • {new Date(notif.created_at).toLocaleDateString()}</p>
                        </div>
                        {notif.is_read ? (
                          <span className="text-xs text-stone-500">Read</span>
                        ) : (
                          <span className="text-xs text-[#EFB342]">Unread</span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <AlertCircle className="text-red-400" size={16} />
                    <p className="text-sm text-red-400">No notifications have been sent to this user</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* COLLAPSIBLE: Welcome Messages Section */}
        <div className="bg-white border border-stone-200 rounded-2xl mb-6 overflow-hidden">
          <button
            onClick={() => setWelcomeSectionOpen(!welcomeSectionOpen)}
            className="w-full p-6 flex items-center justify-between hover:bg-[#F0EBE1] transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                <Mail className="text-green-400" size={24} />
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-stone-900">Welcome Messages</h3>
                <p className="text-sm text-stone-500">Customize welcome messages for new users (4 types)</p>
              </div>
            </div>
            {welcomeSectionOpen ? <ChevronUp className="text-stone-600" size={24} /> : <ChevronDown className="text-stone-600" size={24} />}
          </button>
          
          {welcomeSectionOpen && (
            <div className="px-6 pb-6 space-y-4 border-t border-stone-200 pt-4">
              {/* Welcome Message (Self-Registration) */}
              <div className="bg-stone-50 border border-stone-200 rounded-xl p-4" data-testid="welcome-message-section">
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="flex items-center gap-2">
                    <UserPlus className="text-green-400" size={18} />
                    <h4 className="font-medium text-stone-900 text-sm">Self-Registered Users</h4>
                  </div>
                  {!welcomeEditing ? (
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => sendTestEmail(null, 'welcome')} 
                        disabled={sendingTestId === 'welcome'}
                        className="text-stone-600 hover:text-[#EFB342] h-7 text-xs"
                        title="Send test email"
                      >
                        {sendingTestId === 'welcome' ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setWelcomeEditing(true)} className="text-stone-600 hover:text-stone-900 h-7 text-xs">
                        <Pencil size={14} className="mr-1" /> Edit
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={() => { setWelcomeEditing(false); fetchWelcomeMessage(); }} className="text-stone-600 hover:text-stone-900 h-7 text-xs">Cancel</Button>
                      <Button size="sm" onClick={saveWelcomeMessage} disabled={welcomeSaving} className="bg-[#EFB342] text-black hover:bg-[#D99F30] h-7 text-xs">
                        {welcomeSaving ? <Loader2 className="animate-spin" size={14} /> : 'Save'}
                      </Button>
                    </div>
                  )}
                </div>
                {welcomeEditing ? (
                  <div className="space-y-3">
                    <div>
                      <Label className="text-stone-600 text-xs">Title</Label>
                      <Input value={welcomeMessage.title} onChange={(e) => setWelcomeMessage({ ...welcomeMessage, title: e.target.value })} className="bg-white border-stone-300 mt-1 h-8 text-sm" />
                    </div>
                    <div>
                      <Label className="text-stone-600 text-xs">Message Body</Label>
                      <Textarea value={welcomeMessage.message} onChange={(e) => setWelcomeMessage({ ...welcomeMessage, message: e.target.value })} className="bg-white border-stone-300 mt-1 text-sm" rows={6} />
                      <p className="text-xs text-stone-400 mt-1">Use {'{first_name}'} for the user&apos;s first name</p>
                    </div>
                  </div>
                ) : (
                  <div className="text-xs">
                    <p className="text-stone-600"><span className="text-stone-700">Subject:</span> {welcomeMessage.title}</p>
                    <p className="text-stone-500 mt-1 line-clamp-2">{welcomeMessage.message?.substring(0, 150)}...</p>
                  </div>
                )}
              </div>

              {/* Admin Welcome Message */}
              <div className="bg-stone-50 border border-stone-200 rounded-xl p-4" data-testid="admin-welcome-message-section">
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="flex items-center gap-2">
                    <Settings className="text-orange-400" size={18} />
                    <h4 className="font-medium text-stone-900 text-sm">Admin-Created Users</h4>
                  </div>
                  {!adminWelcomeEditing ? (
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => sendTestEmail(null, 'admin_welcome')} 
                        disabled={sendingTestId === 'admin_welcome'}
                        className="text-stone-600 hover:text-[#EFB342] h-7 text-xs"
                        title="Send test email"
                      >
                        {sendingTestId === 'admin_welcome' ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setAdminWelcomeEditing(true)} className="text-stone-600 hover:text-stone-900 h-7 text-xs">
                        <Pencil size={14} className="mr-1" /> Edit
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={() => { setAdminWelcomeEditing(false); fetchAdminWelcomeMessage(); }} className="text-stone-600 hover:text-stone-900 h-7 text-xs">Cancel</Button>
                      <Button size="sm" onClick={saveAdminWelcomeMessage} disabled={adminWelcomeSaving} className="bg-[#EFB342] text-black hover:bg-[#D99F30] h-7 text-xs">
                        {adminWelcomeSaving ? <Loader2 className="animate-spin" size={14} /> : 'Save'}
                      </Button>
                    </div>
                  )}
                </div>
                {adminWelcomeEditing ? (
                  <div className="space-y-3">
                    <div>
                      <Label className="text-stone-600 text-xs">Title</Label>
                      <Input value={adminWelcomeMessage.title} onChange={(e) => setAdminWelcomeMessage({ ...adminWelcomeMessage, title: e.target.value })} className="bg-white border-stone-300 mt-1 h-8 text-sm" />
                    </div>
                    <div>
                      <Label className="text-stone-600 text-xs">Message Body</Label>
                      <Textarea value={adminWelcomeMessage.message} onChange={(e) => setAdminWelcomeMessage({ ...adminWelcomeMessage, message: e.target.value })} className="bg-white border-stone-300 mt-1 text-sm" rows={6} />
                      <p className="text-xs text-stone-400 mt-1">Use {'{first_name}'} for the user&apos;s first name</p>
                    </div>
                  </div>
                ) : (
                  <div className="text-xs">
                    <p className="text-stone-600"><span className="text-stone-700">Subject:</span> {adminWelcomeMessage.title}</p>
                    <p className="text-stone-500 mt-1 line-clamp-2">{adminWelcomeMessage.message?.substring(0, 150)}...</p>
                  </div>
                )}
              </div>

              {/* Trial Welcome Message */}
              <div className="bg-stone-50 border border-stone-200 rounded-xl p-4" data-testid="trial-welcome-message-section">
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="flex items-center gap-2">
                    <Gift className="text-purple-400" size={18} />
                    <h4 className="font-medium text-stone-900 text-sm">Free Trial Users</h4>
                  </div>
                  {!trialWelcomeEditing ? (
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => sendTestEmail(null, 'trial_welcome')} 
                        disabled={sendingTestId === 'trial_welcome'}
                        className="text-stone-600 hover:text-[#EFB342] h-7 text-xs"
                        title="Send test email"
                      >
                        {sendingTestId === 'trial_welcome' ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setTrialWelcomeEditing(true)} className="text-stone-600 hover:text-stone-900 h-7 text-xs">
                        <Pencil size={14} className="mr-1" /> Edit
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={() => { setTrialWelcomeEditing(false); fetchTrialWelcomeMessage(); }} className="text-stone-600 hover:text-stone-900 h-7 text-xs">Cancel</Button>
                      <Button size="sm" onClick={saveTrialWelcomeMessage} disabled={trialWelcomeSaving} className="bg-[#EFB342] text-black hover:bg-[#D99F30] h-7 text-xs">
                        {trialWelcomeSaving ? <Loader2 className="animate-spin" size={14} /> : 'Save'}
                      </Button>
                    </div>
                  )}
                </div>
                {trialWelcomeEditing ? (
                  <div className="space-y-3">
                    <div>
                      <Label className="text-stone-600 text-xs">Title</Label>
                      <Input value={trialWelcomeMessage.title} onChange={(e) => setTrialWelcomeMessage({ ...trialWelcomeMessage, title: e.target.value })} className="bg-white border-stone-300 mt-1 h-8 text-sm" />
                    </div>
                    <div>
                      <Label className="text-stone-600 text-xs">Message Body</Label>
                      <Textarea value={trialWelcomeMessage.message} onChange={(e) => setTrialWelcomeMessage({ ...trialWelcomeMessage, message: e.target.value })} className="bg-white border-stone-300 mt-1 text-sm" rows={6} />
                      <p className="text-xs text-stone-400 mt-1">Use {'{first_name}'} for the user&apos;s first name. Use ![](gif-url) to add GIFs.</p>
                    </div>
                  </div>
                ) : (
                  <div className="text-xs">
                    <p className="text-stone-600"><span className="text-stone-700">Subject:</span> {trialWelcomeMessage.title}</p>
                    <p className="text-stone-500 mt-1 line-clamp-2">{trialWelcomeMessage.message?.substring(0, 150)}...</p>
                  </div>
                )}
              </div>

              {/* Community Welcome Post (auto-post on Community Board when new user signs up) */}
              <div className="bg-stone-50 border border-stone-200 rounded-xl p-4" data-testid="community-welcome-post-section">
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="flex items-center gap-2">
                    <MessageCircle className="text-[#EFB342]" size={18} />
                    <div>
                      <h4 className="font-medium text-stone-900 text-sm">Community Welcome Post</h4>
                      <p className="text-xs text-stone-500">Auto-posts on the Community Board (from the Coach) whenever a new paid or trial user signs up</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={toggleCommunityWelcome}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${communityWelcome.enabled ? 'bg-[#EFB342]' : 'bg-stone-200'}`}
                      title={communityWelcome.enabled ? 'Enabled — click to disable' : 'Disabled — click to enable'}
                      data-testid="community-welcome-toggle"
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${communityWelcome.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                    {!communityWelcomeEditing ? (
                      <Button variant="ghost" size="sm" onClick={() => setCommunityWelcomeEditing(true)} className="text-stone-600 hover:text-stone-900 h-7 text-xs" data-testid="community-welcome-edit-btn">Edit</Button>
                    ) : (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => { setCommunityWelcomeEditing(false); fetchCommunityWelcome(); }} className="text-stone-600 hover:text-stone-900 h-7 text-xs">Cancel</Button>
                        <Button size="sm" onClick={saveCommunityWelcome} disabled={communityWelcomeSaving} className="bg-[#EFB342] text-black hover:bg-[#D99F30] h-7 text-xs" data-testid="community-welcome-save-btn">
                          {communityWelcomeSaving ? <Loader2 className="animate-spin" size={14} /> : 'Save'}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                {communityWelcomeEditing ? (
                  <div className="space-y-2">
                    <div>
                      <Label className="text-stone-700 text-xs">Post Content</Label>
                      <Textarea
                        value={communityWelcome.content}
                        onChange={(e) => setCommunityWelcome({ ...communityWelcome, content: e.target.value })}
                        className="bg-white border-stone-300 mt-1 text-sm font-mono"
                        rows={14}
                        data-testid="community-welcome-textarea"
                      />
                      <p className="text-xs text-stone-400 mt-1">Use {'{first_name}'} where the new user&apos;s first name should appear.</p>
                    </div>
                  </div>
                ) : (
                  <div className="text-xs">
                    <p className="text-stone-500 line-clamp-3">{(communityWelcome.content || '').substring(0, 240) || 'Click Edit to configure'}...</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* COLLAPSIBLE: System Messages Section (Birthday, Month-End) */}
        <div className="bg-white border border-stone-200 rounded-2xl mb-6 overflow-hidden">
          <button
            onClick={() => setSystemMessagesOpen(!systemMessagesOpen)}
            className="w-full p-6 flex items-center justify-between hover:bg-[#F0EBE1] transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-pink-500/20 rounded-xl flex items-center justify-center">
                <PartyPopper className="text-pink-400" size={24} />
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-stone-900">System Messages</h3>
                <p className="text-sm text-stone-500">Birthday wishes & month-end celebrations</p>
              </div>
            </div>
            {systemMessagesOpen ? <ChevronUp className="text-stone-600" size={24} /> : <ChevronDown className="text-stone-600" size={24} />}
          </button>
          
          {systemMessagesOpen && (
            <div className="px-6 pb-6 space-y-4 border-t border-stone-200 pt-4">
              {/* Birthday Message */}
              <div className="bg-stone-50 border border-stone-200 rounded-xl p-4" data-testid="birthday-message-section">
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="flex items-center gap-2">
                    <Gift className="text-pink-400" size={18} />
                    <div>
                      <h4 className="font-medium text-stone-900 text-sm">Birthday Message</h4>
                      <p className="text-xs text-stone-500">Sent automatically when users log in on their birthday</p>
                    </div>
                  </div>
                  {!birthdayEditing ? (
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => sendTestEmail(null, 'birthday')} 
                        disabled={sendingTestId === 'birthday'}
                        className="text-stone-600 hover:text-[#EFB342] h-7 text-xs"
                        title="Send test email"
                      >
                        {sendingTestId === 'birthday' ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setBirthdayEditing(true)} className="text-stone-600 hover:text-stone-900 h-7 text-xs">
                        <Pencil size={14} className="mr-1" /> Edit
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={() => { setBirthdayEditing(false); fetchBirthdayMessage(); }} className="text-stone-600 hover:text-stone-900 h-7 text-xs">Cancel</Button>
                      <Button size="sm" onClick={saveBirthdayMessage} disabled={birthdaySaving} className="bg-[#EFB342] text-black hover:bg-[#D99F30] h-7 text-xs">
                        {birthdaySaving ? <Loader2 className="animate-spin" size={14} /> : 'Save'}
                      </Button>
                    </div>
                  )}
                </div>
                {birthdayEditing ? (
                  <div className="space-y-3">
                    <div>
                      <Label className="text-stone-600 text-xs">Title</Label>
                      <Input value={birthdayMessage.title} onChange={(e) => setBirthdayMessage({ ...birthdayMessage, title: e.target.value })} className="bg-white border-stone-300 mt-1 h-8 text-sm" />
                    </div>
                    <div>
                      <Label className="text-stone-600 text-xs">Message Body</Label>
                      <Textarea value={birthdayMessage.message} onChange={(e) => setBirthdayMessage({ ...birthdayMessage, message: e.target.value })} className="bg-white border-stone-300 mt-1 text-sm" rows={4} />
                      <p className="text-xs text-stone-400 mt-1">Use {'{first_name}'} for the user&apos;s first name</p>
                    </div>
                  </div>
                ) : (
                  <div className="text-xs">
                    <p className="text-stone-600"><span className="text-stone-700">Subject:</span> {birthdayMessage.title || 'Not configured'}</p>
                    <p className="text-stone-500 mt-1 line-clamp-2">{birthdayMessage.message?.substring(0, 150) || 'Click Edit to configure'}...</p>
                  </div>
                )}
              </div>

              {/* Month-End Celebration Message */}
              <div className="bg-stone-50 border border-stone-200 rounded-xl p-4" data-testid="monthend-message-section">
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="flex items-center gap-2">
                    <Trophy className="text-yellow-400" size={18} />
                    <div>
                      <h4 className="font-medium text-stone-900 text-sm">Month-End Celebration</h4>
                      <p className="text-xs text-stone-500">Sent automatically on the 29th (after challenge ends) to announce winners</p>
                    </div>
                  </div>
                  {!monthEndEditing ? (
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => sendTestEmail(null, 'month_end')} 
                        disabled={sendingTestId === 'month_end'}
                        className="text-stone-600 hover:text-[#EFB342] h-7 text-xs"
                        title="Send test email"
                      >
                        {sendingTestId === 'month_end' ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setMonthEndEditing(true)} className="text-stone-600 hover:text-stone-900 h-7 text-xs">
                        <Pencil size={14} className="mr-1" /> Edit
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={() => { setMonthEndEditing(false); fetchMonthEndMessage(); }} className="text-stone-600 hover:text-stone-900 h-7 text-xs">Cancel</Button>
                      <Button size="sm" onClick={saveMonthEndMessage} disabled={monthEndSaving} className="bg-[#EFB342] text-black hover:bg-[#D99F30] h-7 text-xs">
                        {monthEndSaving ? <Loader2 className="animate-spin" size={14} /> : 'Save'}
                      </Button>
                    </div>
                  )}
                </div>
                {monthEndEditing ? (
                  <div className="space-y-3">
                    <div>
                      <Label className="text-stone-600 text-xs">Title Template</Label>
                      <Input value={monthEndMessage.title} onChange={(e) => setMonthEndMessage({ ...monthEndMessage, title: e.target.value })} className="bg-white border-stone-300 mt-1 h-8 text-sm" placeholder="🎉 {month} Challenge Complete!" />
                      <p className="text-xs text-stone-400 mt-1">Use {'{month}'} for the month name</p>
                    </div>
                    <div>
                      <Label className="text-stone-600 text-xs">Message Body Template</Label>
                      <Textarea value={monthEndMessage.message} onChange={(e) => setMonthEndMessage({ ...monthEndMessage, message: e.target.value })} className="bg-white border-stone-300 mt-1 text-sm" rows={6} />
                      <p className="text-xs text-stone-400 mt-1">
                        Available placeholders: {'{first_name}'}, {'{month}'}, {'{first_place}'}, {'{first_points}'}, {'{second_place}'}, {'{second_points}'}, {'{third_place}'}, {'{third_points}'}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-white rounded-lg">
                      <Switch
                        checked={monthEndMessage.auto_send !== false}
                        onCheckedChange={(checked) => setMonthEndMessage({ ...monthEndMessage, auto_send: checked })}
                      />
                      <div>
                        <p className="text-sm text-stone-900">Auto-send on day 29</p>
                        <p className="text-xs text-stone-500">Automatically send celebration to all paid users after challenge ends</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-xs">
                    <p className="text-stone-600"><span className="text-stone-700">Subject:</span> {monthEndMessage.title || '🎉 {month} Challenge Complete!'}</p>
                    <p className="text-stone-500 mt-1 line-clamp-2">{monthEndMessage.message?.substring(0, 150) || 'Default celebration message'}...</p>
                    <p className="text-xs mt-2">
                      <span className={monthEndMessage.auto_send !== false ? 'text-green-400' : 'text-stone-500'}>
                        {monthEndMessage.auto_send !== false ? '✓ Auto-send enabled' : '○ Manual trigger required'}
                      </span>
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Members-Only Perk Block (appears in footer of all client-facing emails) */}
        <div
          className="bg-white border border-[#EFB342]/40 rounded-2xl overflow-hidden"
          data-testid="member-promo-section"
        >
          <div className="px-6 py-5 flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-[#EFB342]/15 rounded-xl flex items-center justify-center">
                <Gift className="text-[#EFB342]" size={24} />
              </div>
              <div>
                <h3 className="font-semibold text-stone-900">Members-Only Perk Block</h3>
                <p className="text-sm text-stone-500">Shown at the bottom of every client-facing email (welcome, automations, month-end)</p>
              </div>
            </div>
            {!memberPromoEditing ? (
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={resetMemberPromo}
                  disabled={memberPromoSaving}
                  className="text-stone-600 hover:text-stone-900 h-8 text-xs"
                  data-testid="member-promo-reset-btn"
                  title="Reset to default wording"
                >
                  Reset
                </Button>
                <Button
                  size="sm"
                  onClick={() => setMemberPromoEditing(true)}
                  className="bg-[#EFB342] text-black hover:bg-[#D99F30] h-8 text-xs"
                  data-testid="member-promo-edit-btn"
                >
                  <Pencil size={14} className="mr-1" /> Edit
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setMemberPromoEditing(false); fetchMemberPromo(); }}
                  className="text-stone-600 hover:text-stone-900 h-8 text-xs"
                  data-testid="member-promo-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={saveMemberPromo}
                  disabled={memberPromoSaving}
                  className="bg-[#EFB342] text-black hover:bg-[#D99F30] h-8 text-xs"
                  data-testid="member-promo-save-btn"
                >
                  {memberPromoSaving ? <Loader2 className="animate-spin" size={14} /> : 'Save'}
                </Button>
              </div>
            )}
          </div>

          <div className="px-6 pb-6 border-t border-stone-200 pt-4">
            {memberPromoEditing ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between gap-4 bg-stone-50 border border-stone-200 rounded-lg px-4 py-3">
                  <div>
                    <p className="text-sm font-medium text-stone-900">Show in emails</p>
                    <p className="text-xs text-stone-500">Turn off to hide the perk block entirely</p>
                  </div>
                  <Switch
                    checked={memberPromo.enabled}
                    onCheckedChange={(v) => setMemberPromo({ ...memberPromo, enabled: v })}
                    data-testid="member-promo-enabled-switch"
                  />
                </div>
                <div>
                  <Label className="text-stone-600 text-xs">Label (small caps header)</Label>
                  <Input
                    value={memberPromo.label}
                    onChange={(e) => setMemberPromo({ ...memberPromo, label: e.target.value })}
                    className="bg-white border-stone-300 mt-1 h-9 text-sm"
                    placeholder="💛 Members-Only Perk"
                    data-testid="member-promo-label-input"
                  />
                </div>
                <div>
                  <Label className="text-stone-600 text-xs">Body copy</Label>
                  <Textarea
                    value={memberPromo.body}
                    onChange={(e) => setMemberPromo({ ...memberPromo, body: e.target.value })}
                    className="bg-white border-stone-300 mt-1 text-sm"
                    rows={4}
                    placeholder="As an RxGRIT member, you get **50% off..."
                    data-testid="member-promo-body-input"
                  />
                  <p className="text-xs text-stone-400 mt-1">Wrap text in <code className="text-[#EFB342]">**double asterisks**</code> to highlight in gold.</p>
                </div>
                <div>
                  <Label className="text-stone-600 text-xs">Contact email (mailto link)</Label>
                  <Input
                    value={memberPromo.contact_email}
                    onChange={(e) => setMemberPromo({ ...memberPromo, contact_email: e.target.value })}
                    className="bg-white border-stone-300 mt-1 h-9 text-sm"
                    placeholder="yvette@buildnutrition.ca"
                    data-testid="member-promo-email-input"
                  />
                  <p className="text-xs text-stone-400 mt-1">Leave blank to hide the contact link.</p>
                </div>
              </div>
            ) : (
              <div className="bg-stone-50 border border-[#EFB342]/30 rounded-xl px-5 py-4" data-testid="member-promo-preview">
                <p className="text-xs font-bold text-[#EFB342] uppercase tracking-wide mb-2">{memberPromo.label || 'Members-Only Perk'}</p>
                <p className="text-sm text-stone-800 leading-relaxed whitespace-pre-wrap">{memberPromo.body || 'Click Edit to configure the perk block.'}</p>
                {memberPromo.contact_email && (
                  <p className="mt-3 text-sm">
                    <a href={`mailto:${memberPromo.contact_email}`} className="text-[#EFB342] font-bold underline">{memberPromo.contact_email}</a>
                  </p>
                )}
                {!memberPromo.enabled && (
                  <p className="mt-3 text-xs text-red-400">⚠ Currently hidden from emails (toggle off).</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Regular Automations List */}
        <div className="space-y-4">
          <h2 className="text-xl font-bold text-stone-900 flex items-center gap-2">
            <Settings className="text-purple-400" size={20} />
            General Automations
          </h2>
          {automations.filter(a => !a.is_trial_automation).length > 0 ? (
            automations.filter(a => !a.is_trial_automation).map((automation) => (
              <div
                key={automation.id}
                className={`bg-white border rounded-2xl p-6 ${
                  automation.is_active ? 'border-stone-200' : 'border-stone-200/50 opacity-60'
                }`}
                data-testid={`automation-${automation.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      automation.trigger_type === 'scheduled_notification' 
                        ? (automation.is_active ? 'bg-blue-500/20' : 'bg-stone-100')
                        : (automation.is_active ? 'bg-purple-500/20' : 'bg-stone-100')
                    }`}>
                      {automation.trigger_type === 'scheduled_notification' 
                        ? <Calendar className={automation.is_active ? 'text-blue-400' : 'text-stone-500'} size={24} />
                        : <Settings className={automation.is_active ? 'text-purple-400' : 'text-stone-500'} size={24} />
                      }
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-stone-900">{automation.name}</h3>
                        {automation.trigger_type === 'scheduled_notification' && (
                          <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-full">
                            Scheduled
                          </span>
                        )}
                        {automation.trigger_type === 'no_monthly_activity' && (
                          <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded-full">
                            No Activity Nudge
                          </span>
                        )}
                        {!automation.is_active && (
                          <span className="px-2 py-0.5 bg-stone-100 text-stone-500 text-xs rounded-full">
                            Disabled
                          </span>
                        )}
                        {automation.trigger_type === 'missing_action' && automation.require_monthly_activity && (
                          <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">
                            Active Users Only
                          </span>
                        )}
                        {automation.links && automation.links.length > 0 && (
                          <span className="px-2 py-0.5 bg-stone-100 text-stone-600 text-xs rounded-full flex items-center gap-1">
                            <Link size={10} /> {automation.links.length}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-stone-500 mb-2">
                        <Clock size={14} />
                        {automation.trigger_type === 'inactivity' 
                          ? `Triggers after ${automation.trigger_days} days of inactivity`
                          : automation.trigger_type === 'missing_action'
                          ? `Triggers when "${getActionName(automation.action_id)}" missed for ${automation.trigger_days} days`
                          : automation.trigger_type === 'renewal_reminder'
                          ? `Triggers ${automation.trigger_days} days before annual subscription renewal`
                          : automation.trigger_type === 'no_monthly_activity'
                          ? `Nudges no-check-in users on day ${automation.trigger_days} of each month`
                          : `Sends to all users on day ${automation.trigger_days} of each month`
                        }
                      </div>
                      <p className="text-stone-600 text-sm">
                        <span className="font-medium text-stone-900">Subject:</span> {automation.message_subject}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => sendTestEmail(automation.id)}
                      disabled={sendingTestId === automation.id}
                      className="text-stone-600 hover:text-[#EFB342]"
                      title="Send test email to yourself"
                      data-testid={`test-automation-${automation.id}`}
                    >
                      {sendingTestId === automation.id ? (
                        <Loader2 size={18} className="animate-spin" />
                      ) : (
                        <Send size={18} />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEdit(automation)}
                      className="text-stone-600 hover:text-stone-900"
                      data-testid={`edit-automation-${automation.id}`}
                    >
                      <Pencil size={18} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(automation.id)}
                      className="text-stone-600 hover:text-red-500"
                      data-testid={`delete-automation-${automation.id}`}
                    >
                      <Trash2 size={18} />
                    </Button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8 bg-white border border-stone-200 rounded-2xl">
              <Settings className="mx-auto text-stone-400 mb-3" size={36} />
              <p className="text-stone-500">No general automations configured</p>
              <p className="text-stone-400 text-sm">Set up automated messages to re-engage users</p>
            </div>
          )}
        </div>

        {/* Free Trial Automations Section */}
        <div className="space-y-4 mt-12 pt-8 border-t border-stone-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-stone-900 flex items-center gap-2">
                <UserPlus className="text-green-400" size={20} />
                Free Trial User Automations
              </h2>
              <p className="text-stone-500 text-sm mt-1">
                Automated messages sent to trial users at specific days of their trial journey
              </p>
            </div>
            {automations.filter(a => a.is_trial_automation).length === 0 && (
              <Button
                onClick={seedTrialAutomations}
                disabled={seedingTrialAutomations}
                className="bg-green-600 hover:bg-green-700 text-stone-900"
              >
                {seedingTrialAutomations ? (
                  <Loader2 size={16} className="mr-2 animate-spin" />
                ) : (
                  <Plus size={16} className="mr-2" />
                )}
                Create Default Trial Automations
              </Button>
            )}
          </div>

          {automations.filter(a => a.is_trial_automation).length > 0 ? (
            automations.filter(a => a.is_trial_automation).map((automation) => (
              <div
                key={automation.id}
                className={`bg-gradient-to-r from-green-900/20 to-emerald-900/20 border rounded-2xl p-6 ${
                  automation.is_active ? 'border-green-700/50' : 'border-stone-200/50 opacity-60'
                }`}
                data-testid={`trial-automation-${automation.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      automation.is_active ? 'bg-green-500/20' : 'bg-stone-100'
                    }`}>
                      <UserPlus className={automation.is_active ? 'text-green-400' : 'text-stone-500'} size={24} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-stone-900">{automation.name}</h3>
                        <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">
                          Trial Day {automation.trigger_days}
                        </span>
                        {automation.target_audience === 'unconverted_trial' && (
                          <span className="px-2 py-0.5 bg-orange-500/20 text-orange-400 text-xs rounded-full">
                            Unconverted Only
                          </span>
                        )}
                        {!automation.is_active && (
                          <span className="px-2 py-0.5 bg-stone-100 text-stone-500 text-xs rounded-full">
                            Disabled
                          </span>
                        )}
                        {automation.links && automation.links.length > 0 && (
                          <span className="px-2 py-0.5 bg-stone-100 text-stone-600 text-xs rounded-full flex items-center gap-1">
                            <Link size={10} /> {automation.links.length}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-stone-600 mb-2">
                        <Clock size={14} />
                        Sends on day {automation.trigger_days} of user&apos;s trial
                        {automation.target_audience === 'unconverted_trial' && ' (only if not yet converted to paid)'}
                      </div>
                      <p className="text-stone-600 text-sm">
                        <span className="font-medium text-stone-900">Subject:</span> {automation.message_subject}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => sendTestEmail(automation.id)}
                      disabled={sendingTestId === automation.id}
                      className="text-stone-600 hover:text-[#EFB342]"
                      title="Send test email to yourself"
                      data-testid={`test-trial-automation-${automation.id}`}
                    >
                      {sendingTestId === automation.id ? (
                        <Loader2 size={18} className="animate-spin" />
                      ) : (
                        <Send size={18} />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEdit(automation)}
                      className="text-stone-600 hover:text-stone-900"
                      data-testid={`edit-trial-automation-${automation.id}`}
                    >
                      <Pencil size={18} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(automation.id)}
                      className="text-stone-600 hover:text-red-500"
                      data-testid={`delete-trial-automation-${automation.id}`}
                    >
                      <Trash2 size={18} />
                    </Button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-12 bg-gradient-to-r from-green-900/10 to-emerald-900/10 border border-green-800/30 rounded-2xl">
              <UserPlus className="mx-auto text-green-600 mb-4" size={48} />
              <p className="text-stone-600 mb-2">No trial automations configured yet</p>
              <p className="text-stone-500 text-sm mb-4">
                Click the button above to create the default trial journey automations
              </p>
              <p className="text-stone-400 text-xs">
                This will create: Day 5 (2 days left), Day 7 (expires today), Day 14 (final offer)
              </p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default AdminAutomations;
